import React, { useEffect, useState, useContext, useRef } from "react";
import Header from "../../elements/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  doc,
  getDoc,
  query,
  getDocs,
  collection,
} from "firebase/firestore";
import { useFirestore } from "../../firebase/FirestoreContext";
import { collectionPath } from "../../context/EnvContext";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import { UserContext } from "../../context/UserContext";
import StickyFooter from "../../elements/StickyFooter";

const zelleHandle = "@getfullproof";
const venmoHandle = "@getfullproof";

const PaymentRequest = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const firestore = useFirestore();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCartID, setCurrentCartID] = useState(null);
  const [showStickyFooter, setShowStickyFooter] = useState(false);

  const [searchParams] = useSearchParams();
  const cartId = searchParams.get("cart_id");

  const continueShoppingRef = useRef(null);

  const handleContinueShopping = () => navigate("/");

  useEffect(() => {
    function checkVisibility() {
      if (!continueShoppingRef.current) return;
      const rect = continueShoppingRef.current.getBoundingClientRect();
      setShowStickyFooter(!(rect.bottom > 0 && rect.top < window.innerHeight));
    }
    window.addEventListener("scroll", checkVisibility, { passive: true });
    window.addEventListener("resize", checkVisibility);
    const timeoutId = setTimeout(checkVisibility, 50);
    return () => {
      window.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("resize", checkVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!continueShoppingRef.current) return;
      const rect = continueShoppingRef.current.getBoundingClientRect();
      setShowStickyFooter(!(rect.bottom > 0 && rect.top < window.innerHeight));
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!cartId) {
        setError("No order ID found.");
        setLoading(false);
        return;
      }

      try {
        const cartDocRef = doc(
          firestore,
          `${collectionPath}Carts/CartsList/DataBase`,
          cartId,
        );
        const cartDocSnapshot = await getDoc(cartDocRef);

        if (!cartDocSnapshot.exists()) {
          setError("Order not found.");
          setLoading(false);
          return;
        }

        const orderData = cartDocSnapshot.data();
        orderData.cartID = cartDocSnapshot.id;

        const itemsSnapshot = await getDocs(
          query(
            collection(
              firestore,
              `${collectionPath}Carts/CartsList/DataBase/${cartId}/Items`,
            ),
          ),
        );

        orderData.items = itemsSnapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        const totalPrice = (
          (orderData.subtotal || 0) -
          (orderData.applied_discount_amount || 0) +
          (orderData.shippingCost || 0)
        ).toFixed(2);

        setCurrentCartID(orderData.cart_sooperstock_ID);
        setOrderDetails({ ...orderData, totalPrice });
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error fetching order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [cartId, firestore]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const { items: orderedItems, shippingCost = 0, manualPaymentMethod } = orderDetails;

  const paymentLabel = manualPaymentMethod === "venmo" ? "Venmo" : "Zelle";
  const paymentHandle = manualPaymentMethod === "venmo" ? venmoHandle : zelleHandle;

  return (
    <div style={styles.container}>
      <Header search={false} />
      <div style={styles.content}>
        <div style={styles.animationContainer}>
          <dotlottie-player
            src="https://lottie.host/c86b1f88-9cf7-474c-8075-512aeecf1874/EQZ94rIPgj.json"
            background="transparent"
            speed="1"
            autoplay
            style={{ height: "200px", width: "200px" }}
          ></dotlottie-player>
        </div>

        <h2 style={{ ...styles.title, marginBottom: 20 }}>
          Order Request Submitted!
        </h2>

        <div
          style={{
            background: "#f5f5f5",
            padding: "20px",
            borderRadius: 12,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontSize: 14, color: "#666" }}>
            Your Order #
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            {currentCartID}
          </p>
        </div>

        <div
          style={{
            background: "#f0f4ff",
            border: "1px solid #3b72fe",
            borderRadius: 12,
            padding: "20px",
            marginBottom: 20,
            textAlign: "left",
          }}
        >
          <h3 style={{ margin: "0 0 12px 0", fontSize: 16 }}>
            How to complete your order
          </h3>
          <p style={{ margin: "0 0 10px 0", fontSize: 15 }}>
            Send <strong>${orderDetails.totalPrice}</strong> via{" "}
            <strong>{paymentLabel}</strong> to{" "}
            <strong>{paymentHandle}</strong>.
          </p>
          <p style={{ margin: "0 0 16px 0", fontSize: 15 }}>
            <strong>Important:</strong> Include your Order #{" "}
            <strong>{currentCartID}</strong> in the memo/notes so we can match
            your payment.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#555" }}>
            We typically confirm within minutes during business hours
            (Mon–Fri, 9am–5pm PT). Payments outside of business hours will be
            confirmed by the next business day. You&apos;ll receive an order
            confirmation email once payment is confirmed.
          </p>
        </div>

        <div
          style={{
            background: "#f5f5f5",
            padding: "20px",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <h3 style={{ ...styles.subtitle }}>Items Ordered:</h3>
          <ul style={styles.itemsList}>
            {orderedItems &&
              orderedItems.map((item, index) => (
                <li
                  key={index}
                  style={{
                    ...styles.item,
                    display: "flex",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                  }}
                >
                  <div style={styles.itemDetails}>
                    <img
                      src={
                        item?.images?.length > 0
                          ? item.images[0]
                          : "/images/no_product_image.png"
                      }
                      alt={item.product_name}
                      style={styles.itemImage}
                    />
                    <div>
                      <p style={styles.itemName}>{item?.brand_name}</p>
                      <p style={styles.itemName}>{item.product_name}</p>
                      <p style={{ ...styles.itemName, textAlign: "left" }}>
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p style={styles.itemPrice}>
                    ${(item.final_price * item.quantity).toFixed(2)}
                  </p>
                </li>
              ))}

            {shippingCost > 0 && (
              <li style={styles.item}>
                <div style={styles.itemDetails}>
                  <p style={{ ...styles.itemName, padding: 0, textAlign: "left" }}>
                    Shipping
                  </p>
                </div>
                <p style={styles.itemPrice}>
                  ${parseFloat(shippingCost).toFixed(2)}
                </p>
              </li>
            )}

            <li
              style={{
                ...styles.item,
                borderBottom: "none",
                paddingBottom: 0,
                marginBottom: 0,
              }}
            >
              <div style={styles.itemDetails}>
                <p style={{ ...styles.itemName, padding: 0, textAlign: "left" }}>
                  Total
                </p>
              </div>
              <p style={styles.itemPrice}>${orderDetails.totalPrice}</p>
            </li>
          </ul>
        </div>

        <button
          ref={continueShoppingRef}
          style={styles.continueButton}
          onClick={handleContinueShopping}
        >
          Continue Shopping
        </button>
      </div>
      <FAQ />
      <Footer />
      {showStickyFooter && (
        <StickyFooter
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100vw",
            padding: "12px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 600, paddingLeft: 12, paddingRight: 12 }}>
            <button
              style={{ ...styles.continueButton, width: "100%" }}
              onClick={handleContinueShopping}
            >
              Continue Shopping
            </button>
          </div>
        </StickyFooter>
      )}
    </div>
  );
};

const styles = {
  container: { backgroundColor: "#fff", minHeight: "100vh" },
  content: {
    padding: "20px",
    maxWidth: "600px",
    margin: "0 auto",
    textAlign: "center",
    fontFamily: "'Inter', sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  animationContainer: { marginBottom: "20px", alignSelf: "center" },
  title: { fontSize: "24px", marginBottom: "10px" },
  subtitle: { fontSize: "1rem", marginBottom: "20px", textAlign: "left" },
  itemsList: { listStyleType: "none", padding: "0", marginBottom: "20px" },
  item: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    borderBottom: "1px solid #ddd",
    paddingBottom: "10px",
  },
  itemDetails: { display: "flex", alignItems: "center" },
  itemImage: {
    width: "50px",
    height: "50px",
    marginRight: "15px",
    objectFit: "cover",
    borderRadius: "5px",
  },
  itemName: { textAlign: "left", fontSize: "16px", marginBottom: "5px" },
  itemPrice: { fontSize: "16px", fontWeight: "bold" },
  continueButton: {
    width: "100%",
    padding: "12px",
    backgroundColor: "black",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    borderRadius: "50px",
  },
};

export default PaymentRequest;
